import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { companyChanged } from 'store/actions/companyActions';
import type { ThunkConf } from 'store/thunks';
import type { RootState } from 'store';
import services from 'services';
import axios from 'axios';

type SkuLocationMap = {
    [sku: string]: string;
};

type InitialState = {
    locations: SkuLocationMap;
    loading: boolean;
    error: string | null;
    /** requestId of the most recent fetch, used to discard superseded responses */
    currentRequestId: string | null;
};

const initialState: InitialState = {
    locations: {},
    loading: false,
    error: null,
    currentRequestId: null,
};

// Thunks
const fetchSkuLocations = createAsyncThunk<
    { [key: string]: string },
    string[],
    ThunkConf
>('skuLocation/fetchSkuLocations', async (skus, { rejectWithValue, signal }) => {
    try {
        if (skus.length === 0) {
            return {};
        }

        const data = await services.skuLocation.getSkuLocationsBySkus(skus, signal);
        return data;
    } catch (error) {
        if (axios.isCancel(error)) {
            return rejectWithValue(undefined);
        }
        return rejectWithValue('Failed to fetch SKU locations');
    }
});

const skuLocationSlice = createSlice({
    name: 'skuLocation',
    initialState,
    reducers: {
        clearSkuLocations: (state) => {
            state.locations = {};
            state.error = null;
            state.currentRequestId = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSkuLocations.pending, (state, action) => {
                state.loading = true;
                state.error = null;
                state.currentRequestId = action.meta.requestId;
            })
            .addCase(fetchSkuLocations.fulfilled, (state, action) => {
                // Replaced wholesale, so a late stale response would drop newer locations.
                if (state.currentRequestId !== action.meta.requestId) {
                    return;
                }
                state.loading = false;
                state.currentRequestId = null;
                state.locations = action.payload;
            })
            .addCase(fetchSkuLocations.rejected, (state, action) => {
                if (state.currentRequestId !== action.meta.requestId) {
                    return;
                }
                state.loading = false;
                state.currentRequestId = null;
                if (action.payload) {
                    state.error = action.payload as string;
                }
            });

        // Reset on company change
        builder.addCase(companyChanged, () => initialState);
    },
});

// Selectors
const selectSkuLocationState = (state: RootState) => state.skuLocation;

const selectSkuLocations = createSelector(
    selectSkuLocationState,
    (skuLocation) => skuLocation.locations
);

const selectSkuLocationBySku = (sku: string | undefined | null) =>
    createSelector(selectSkuLocations, (locations) => (sku ? locations[sku] : undefined));

const selectSkuLocationLoading = createSelector(
    selectSkuLocationState,
    (skuLocation) => skuLocation.loading
);

// Exports
export const skuLocationThunks = {
    fetchSkuLocations,
};

export const skuLocationSelectors = {
    selectSkuLocations,
    selectSkuLocationBySku,
    selectSkuLocationLoading,
};

export const { clearSkuLocations } = skuLocationSlice.actions;

export default skuLocationSlice.reducer;
